import React, { useState, useEffect, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import "./styles.css";
import Container from "../../../components/Container";
import { Table, Button, Input, Space, Modal, notification, Tag } from "antd";
import { db } from "../../../services/firebase";
import { collection, getDocs, deleteDoc, doc } from "firebase/firestore";
import { EyeOutlined, EditOutlined, DeleteOutlined, SearchOutlined, SafetyCertificateOutlined, WarningOutlined, PlusOutlined } from "@ant-design/icons";

function CertificateList() {
  const [certificados, setCertificados] = useState([]);
  const [certificadosFilter, setCertificadosFilter] = useState([]);

  const location = useLocation();
  const globalSearch = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("search") || "";
  }, [location.search]);

  // 🔎 filtro geral vindo do header (TAG, Nº Série, Cliente, Unidade, Tipo, Nº Certificado, SPL)
  const applyGlobalFilter = (list) => {
    if (!globalSearch) return list;

    const term = globalSearch.toLowerCase();

    return list.filter((item) => {
      const campos = [
        item.tag,
        item.numeroSerie,
        item.nomeEmpresa,
        item.unidadeCliente,
        item.tipoValvula,
        item.tipoCertificado,
        item.numeroCertificado,
        item.spl,
      ];

      return campos.some((campo) =>
        campo?.toString().toLowerCase().includes(term)
      );
    });
  };

  function handleSearch(search, key) {
    const searchTerm = search?.toLowerCase() || "";
    const baseList = applyGlobalFilter(certificados); // mantém filtro geral
    const data = baseList.filter((item) => {
      const value = item[key];
      return value?.toString().toLowerCase().includes(searchTerm);
    });
    setCertificadosFilter(search ? data : baseList);
  }

  function reset() {
    setCertificadosFilter(applyGlobalFilter(certificados));
  }

  const handleDelete = async (id, tag) => {
    Modal.confirm({
      title: "Deseja excluir este certificado?",
      content: `O certificado da válvula "${tag}" será removido permanentemente.`,
      okText: "Sim, excluir",
      okType: "danger",
      cancelText: "Cancelar",
      onOk: async () => {
        try {
          await deleteDoc(doc(db, "certificados", id));
          notification.success({
            message: "Certificado excluído!",
            description: "O certificado foi removido com sucesso.",
            placement: "topRight",
          });
          fetchValvesFirebase();
        } catch (error) {
          console.error("Erro ao excluir certificado:", error);
          notification.error({
            message: "Erro ao excluir",
            description: "Não foi possível excluir o certificado.",
            placement: "topRight",
          });
        }
      },
    });
  };

  const columns = [
    {
      title: "Nº",
      dataIndex: "numeroCertificado",
      key: "numeroCertificado",
      filterDropdown: ({ setSelectedKeys, selectedKeys, close }) => (
        <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
          <Input
            placeholder="Filtrar por Nº Certificado"
            value={selectedKeys[0]}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            style={{ marginBottom: 8, width: 250 }}
          />
          <Space style={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              type="primary"
              onClick={() => {
                handleSearch(selectedKeys[0], "numeroCertificado");
                close();
              }}
              icon={<SearchOutlined />}
              size="small"
            >
              Filtrar
            </Button>
            <Button
              onClick={() => {
                setSelectedKeys([]);
                reset();
                close();
              }}
              size="small"
            >
              Limpar
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered) => (
        <SearchOutlined
          style={{ color: filtered ? "#1677ff" : undefined, fontSize: 16 }}
        />
      ),
    },
    {
      title: "TAG",
      dataIndex: "tag",
      key: "tag",
      render: (tag, record) => {
        if (!tag || !record.hasValvula) {
          return <Tag color="orange" icon={<WarningOutlined />}>N/I</Tag>;
        }
        return tag;
      },
      filterDropdown: ({ setSelectedKeys, selectedKeys, close }) => (
        <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
          <Input
            placeholder="Filtrar por Tag"
            value={selectedKeys[0]}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            style={{ marginBottom: 8, width: 250 }}
          />
          <Space style={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              type="primary"
              onClick={() => {
                handleSearch(selectedKeys[0], "tag");
                close();
              }}
              icon={<SearchOutlined />}
              size="small"
            >
              Filtrar
            </Button>
            <Button
              onClick={() => {
                setSelectedKeys([]);
                reset();
                close();
              }}
              size="small"
            >
              Limpar
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered) => (
        <SearchOutlined
          style={{ color: filtered ? "#1677ff" : undefined, fontSize: 16 }}
        />
      ),
    },
    {
      title: "Nº Série",
      dataIndex: "numeroSerie",
      key: "numeroSerie",
      render: (numeroSerie, record) => {
        if (!numeroSerie || !record.hasValvula) {
          return <Tag color="orange" icon={<WarningOutlined />}>N/I</Tag>;
        }
        return numeroSerie;
      },
      filterDropdown: ({ setSelectedKeys, selectedKeys, close }) => (
        <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
          <Input
            placeholder="Filtrar por Nº Série"
            value={selectedKeys[0]}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            style={{ marginBottom: 8, width: 250 }}
          />
          <Space style={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              type="primary"
              onClick={() => {
                handleSearch(selectedKeys[0], "numeroSerie");
                close();
              }}
              icon={<SearchOutlined />}
              size="small"
            >
              Filtrar
            </Button>
            <Button
              onClick={() => {
                setSelectedKeys([]);
                reset();
                close();
              }}
              size="small"
            >
              Limpar
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered) => (
        <SearchOutlined
          style={{ color: filtered ? "#1677ff" : undefined, fontSize: 16 }}
        />
      ),
    },
    {
      title: "Cliente",
      dataIndex: "nomeEmpresa",
      key: "nomeEmpresa",
      render: (nomeEmpresa, record) => {
        if (!nomeEmpresa || !record.hasClient) {
          return <Tag color="orange" icon={<WarningOutlined />}>N/I</Tag>;
        }
        return nomeEmpresa;
      },
      filterDropdown: ({ setSelectedKeys, selectedKeys, close }) => (
        <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
          <Input
            placeholder="Filtrar por Empresa"
            value={selectedKeys[0]}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            style={{ marginBottom: 8, width: 250 }}
          />
          <Space style={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              type="primary"
              onClick={() => {
                handleSearch(selectedKeys[0], "nomeEmpresa");
                close();
              }}
              icon={<SearchOutlined />}
              size="small"
            >
              Filtrar
            </Button>
            <Button
              onClick={() => {
                setSelectedKeys([]);
                reset();
                close();
              }}
              size="small"
            >
              Limpar
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered) => (
        <SearchOutlined
          style={{ color: filtered ? "#1677ff" : undefined, fontSize: 16 }}
        />
      ),
    },
    {
      title: "Unidade",
      dataIndex: "unidadeCliente",
      key: "unidadeCliente",
      render: (unidadeCliente, record) => {
        if (!unidadeCliente || !record.hasClient) {
          return <Tag color="orange" icon={<WarningOutlined />}>N/I</Tag>;
        }
        return unidadeCliente;
      },
    },
    {
      title: "Tipo de Válvula",
      dataIndex: "tipoValvula",
      key: "tipoValvula",
      render: (tipoValvula, record) => {
        if (!tipoValvula || !record.hasValvula) {
          return <Tag color="orange" icon={<WarningOutlined />}>N/I</Tag>;
        }
        return tipoValvula;
      },
    },
    {
      title: "SPL",
      dataIndex: "spl",
      key: "spl",
    },
    {
      title: "Ações",
      dataIndex: "id",
      key: "actions",
      width: 140,
      render: (id, record) => (
        <div className="actions-cell">
          <Link to={`/admin/cadastro-certificados/${id}`} className="action-btn view">
            <EyeOutlined />
          </Link>
          <Link to={`/admin/cadastro-certificados/${id}`} className="action-btn edit">
            <EditOutlined />
          </Link>
          <button
            onClick={() => handleDelete(id, record.tag)}
            className="action-btn delete"
          >
            <DeleteOutlined />
          </button>
        </div>
      ),
    },
  ];

  const fetchClientsFirebase = async () => {
    const snapshot = await getDocs(collection(db, "clientes"));
    const data = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
    return data;
  };

  const fetchAllValvulas = async () => {
    const [psvSnapshot, controleSnapshot, bloqueioSnapshot] = await Promise.all([
      getDocs(collection(db, "valvulasPSV")),
      getDocs(collection(db, "valvulasControle")),
      getDocs(collection(db, "valvulasBloqueio")),
    ]);

    const allValvulas = [
      ...psvSnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })),
      ...controleSnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })),
      ...bloqueioSnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })),
    ];

    return allValvulas;
  };

  const fetchValvesFirebase = async () => {
    try {
      const snapshot = await getDocs(collection(db, "certificados"));
      const certificadosList = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      const clients = await fetchClientsFirebase();
      const valvulas = await fetchAllValvulas();

      const data = certificadosList.map((cert) => {
        const client = clients.find((c) => c.id === cert.cliente_id);
        const valvula = valvulas.find((v) => v.id === cert.valvula_id);

        // Verificar se é um certificado órfão
        const isOrphan = !client || !valvula;

        return {
          ...cert,
          nomeEmpresa: client?.nomeEmpresa || null,
          unidadeCliente: cert.unidadeCliente || valvula?.unidadeCliente || null,
          isOrphan,
          hasClient: !!client,
          hasValvula: !!valvula,
        };
      });

      // Ordenar por data de criação (mais novos primeiro)
      const orderedData = data.sort((a, b) => {
        const dateA = a.dataCadastro?.toDate?.() || a.dataCadastro?.seconds ? new Date(a.dataCadastro.seconds * 1000) : new Date(0);
        const dateB = b.dataCadastro?.toDate?.() || b.dataCadastro?.seconds ? new Date(b.dataCadastro.seconds * 1000) : new Date(0);
        return dateB - dateA;
      });

      setCertificados(orderedData);
      setCertificadosFilter(applyGlobalFilter(orderedData));
    } catch (error) {
      console.error("Erro ao buscar certificados:", error);
    }
  };

  useEffect(() => {
    fetchValvesFirebase();
  }, []);

  useEffect(() => {
    setCertificadosFilter(applyGlobalFilter(certificados));
  }, [globalSearch, certificados]);

  return (
    <Container>
      <div className="certificate-list-container">
        <div className="list-card">
          <div className="list-header">
            <div className="list-header-left">
              <div className="list-icon">
                <SafetyCertificateOutlined />
              </div>
              <div>
                <h2 className="list-title">Certificados Cadastrados</h2>
                <p className="list-subtitle">{certificadosFilter.length} certificados encontrados</p>
              </div>
            </div>
          </div>

          <Table
            dataSource={certificadosFilter}
            columns={columns}
            rowKey="id"
            className="custom-table"
          />
        </div>

        <Link to="/admin/cadastro-certificado" className="fab-button" title="Novo Certificado">
          <PlusOutlined />
        </Link>
      </div>
    </Container>
  );
}

export default CertificateList;
